<template>
  <section class="ps" :aria-label="`Ситуация: ${title}`">
    <div class="ps-top">
      <p class="ps-kicker">{{ title }}</p>
      <span v-if="fresh.text" :class="['ps-fresh', `is-${fresh.state}`]" role="status">
        <i aria-hidden="true"></i>{{ fresh.text }}
      </span>
    </div>

    <h3 v-if="flow" class="ps-situation">{{ situationText(flow, noun) }}</h3>
    <h3 v-else class="ps-situation ps-situation--muted">{{ failed ? 'Данные не получены — показатели скрыты, чтобы не вводить в заблуждение.' : 'Собираем картину…' }}</h3>

    <div v-if="flow" class="ps-tiles">
      <button
        v-for="(t, i) in tiles"
        :key="t.key"
        type="button"
        :class="['ps-tile', `ps-tile--${t.key}`, { 'is-active': activeLight === t.key, 'is-zero': !t.count }]"
        :style="{ animationDelay: `${i * 70}ms` }"
        :title="t.hint"
        @click="$emit('light', activeLight === t.key ? '' : t.key)"
      >
        <span class="ps-tile__label"><i aria-hidden="true"></i>{{ t.title }}</span>
        <b class="ps-tile__n"><CountUp :value="t.count" /></b>
        <span v-if="t.money" class="ps-tile__money"><CountUp :value="t.money" :format="money" /></span>
        <span v-else class="ps-tile__money ps-muted">{{ t.hint }}</span>
      </button>
    </div>

    <Transition name="ps-swap" mode="out-in">
      <article v-if="priority" :key="priority.id" :class="['ps-focus', `ps-focus--${priority.light}`]">
        <p class="ps-focus__kicker">Главное сейчас</p>
        <h4 class="ps-focus__title">№{{ priority.id }} · {{ priority.title }}</h4>
        <ol class="ps-chain">
          <li><span>Этап</span>{{ priority.status_title }}</li>
          <li v-if="priority.deviation"><span>Что не так</span>{{ priority.deviation }}</li>
          <li v-if="priority.consequence"><span>На что влияет</span>{{ priority.consequence }}</li>
          <li v-if="priority.responsible"><span>Кто действует</span>{{ priority.responsible }}</li>
          <li v-if="priority.next_step" class="ps-chain__next"><span>Что сделать</span>{{ priority.next_step }}</li>
        </ol>
        <button type="button" class="ps-btn" @click="$emit('open', priority.id)">Открыть и решить →</button>
      </article>
    </Transition>
  </section>
</template>

<script setup>
// Рассказ процесса: что происходит → что требует внимания → на что влияет → что делать. Общий компонент. [Claude]
import { computed } from 'vue'
import CountUp from '@/components/process/CountUp.vue'
import { freshness, situationText, storyTiles } from '@/components/process/processFlow.mjs'

const props = defineProps({
  title: { type: String, required: true },
  flow: { type: Object, default: null },
  items: { type: Array, default: () => [] },
  generatedAt: { type: String, default: '' },
  failed: { type: Boolean, default: false },
  activeLight: { type: String, default: '' },
  noun: { type: Object, default: () => ({ one: 'дело', few: 'дела', many: 'дел' }) },
  money: { type: Function, default: (v) => `${Math.round(v).toLocaleString('ru-RU')} ₽` },
  now: { type: Number, default: () => Date.now() },
})
defineEmits(['open', 'light'])

const tiles = computed(() => storyTiles(props.flow))
const priority = computed(() => (props.items || []).find((i) => i.id === props.flow?.priority) || null)
const fresh = computed(() => freshness(props.generatedAt, { nowMs: props.now, failed: props.failed }))
</script>

<style scoped>
.ps { --ink: #1f2430; --muted: #6b7280; --line: #ebe7dc; --gold: #f2b705; --gold-2: #ffe08a;
  --red: #dc2626; --yellow: #d97706; --green: #16a34a; --grey: #9ca3af;
  display: flex; flex-direction: column; gap: 12px; padding: 18px; border-radius: 18px; color: var(--ink);
  background: radial-gradient(120% 140% at 100% 0%, #fff8e3 0%, #fffdf8 45%, #fbfaf6 100%); border: 1px solid var(--line);
  animation: ps-in .45s cubic-bezier(.2, .7, .2, 1) both; }
.ps-top { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.ps-kicker { margin: 0; font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #8a6d00; font-weight: 700; }
.ps-fresh { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); padding: 3px 10px; border-radius: 999px; background: #f4f1e8; }
.ps-fresh i { width: 7px; height: 7px; border-radius: 50%; background: var(--green); }
.ps-fresh.is-stale { background: #fff7ed; color: #9a3412; } .ps-fresh.is-stale i { background: var(--yellow); }
.ps-fresh.is-error { background: #fef2f2; color: #991b1b; } .ps-fresh.is-error i { background: var(--red); animation: ps-pulse 1.4s infinite; }
.ps-situation { margin: 0; font-size: 21px; line-height: 1.3; font-weight: 700; letter-spacing: -.01em; }
.ps-situation--muted { color: var(--muted); font-weight: 500; font-size: 16px; }
.ps-muted { color: var(--muted); }
.ps-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; }
.ps-tile { position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 12px 14px; border-radius: 14px;
  border: 1px solid var(--line); background: #fff; font: inherit; color: inherit; cursor: pointer; text-align: left; overflow: hidden;
  transition: transform .2s, box-shadow .2s, border-color .2s; animation: ps-rise .5s cubic-bezier(.2, .7, .2, 1) both; }
.ps-tile::before { content: ''; position: absolute; inset: 0 auto 0 0; width: 4px; background: var(--grey); }
.ps-tile:hover { transform: translateY(-2px); box-shadow: 0 14px 26px -20px rgba(31, 36, 48, .6); }
.ps-tile:focus-visible, .ps-btn:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.ps-tile.is-active { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(242, 183, 5, .2); }
.ps-tile.is-zero { opacity: .7; }
.ps-tile__label { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #374151; font-weight: 600; }
.ps-tile__label i { width: 9px; height: 9px; border-radius: 50%; background: var(--grey); }
.ps-tile__n { font-size: 28px; line-height: 1.05; font-weight: 800; letter-spacing: -.02em; }
.ps-tile__money { font-size: 12.5px; }
.ps-tile--red::before, .ps-tile--red .ps-tile__label i { background: var(--red); } .ps-tile--red .ps-tile__n { color: var(--red); }
.ps-tile--yellow::before, .ps-tile--yellow .ps-tile__label i { background: var(--yellow); } .ps-tile--yellow .ps-tile__n { color: #b45309; }
.ps-tile--green::before, .ps-tile--green .ps-tile__label i { background: var(--green); }
.ps-focus { position: relative; padding: 14px 16px; border-radius: 16px; background: #fff; border: 1px solid var(--line); display: flex; flex-direction: column; gap: 8px; }
.ps-focus--red { border-color: rgba(220, 38, 38, .35); box-shadow: 0 0 0 0 rgba(220, 38, 38, .25); animation: ps-alert 2.4s ease-in-out infinite; }
.ps-focus--yellow { border-color: rgba(217, 119, 6, .35); }
.ps-focus__kicker { margin: 0; font-size: 11.5px; letter-spacing: .08em; text-transform: uppercase; color: var(--red); font-weight: 800; }
.ps-focus--yellow .ps-focus__kicker { color: var(--yellow); }
.ps-focus__title { margin: 0; font-size: 16px; }
.ps-chain { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
.ps-chain li { display: grid; grid-template-columns: 120px 1fr; gap: 10px; font-size: 13.5px; line-height: 1.45; }
.ps-chain li span { color: var(--muted); font-size: 12.5px; }
.ps-chain__next { padding: 8px 10px; margin: 2px -10px 0; border-radius: 10px; background: #fff8e1; font-weight: 600; }
.ps-btn { align-self: flex-start; padding: 9px 16px; border: 0; border-radius: 999px; font: inherit; font-weight: 700; font-size: 13.5px; cursor: pointer;
  color: var(--ink); background: linear-gradient(135deg, var(--gold-2), var(--gold)); transition: transform .2s, box-shadow .2s; }
.ps-btn:hover { transform: translateX(2px); box-shadow: 0 10px 22px -12px rgba(242, 183, 5, .95); }
.ps-swap-enter-active, .ps-swap-leave-active { transition: opacity .25s, transform .25s; }
.ps-swap-enter-from { opacity: 0; transform: translateY(6px); } .ps-swap-leave-to { opacity: 0; transform: translateY(-6px); }
@keyframes ps-in { from { opacity: 0; transform: translateY(8px); } }
@keyframes ps-rise { from { opacity: 0; transform: translateY(10px) scale(.98); } }
@keyframes ps-pulse { 50% { opacity: .35; } }
@keyframes ps-alert { 50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, .07); } }
@media (max-width: 640px) { .ps-chain li { grid-template-columns: 1fr; gap: 0; } .ps-situation { font-size: 18px; } }
@media (prefers-reduced-motion: reduce) { .ps, .ps-tile, .ps-focus--red, .ps-fresh i { animation: none; } .ps-swap-enter-active, .ps-swap-leave-active { transition: none; } }
</style>
